import { createFileRoute, useParams } from '@tanstack/react-router';
import { useCallback, useEffect, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { useStore } from '../../../store';
import layoutStyles from '../../../components/ProjectsView.module.css';
import styles from '../../../components/ProjectSecurity.module.css';
import { waitForAck } from '../../../utils/waitForAck';
import { send, waitForReady } from '../../../ws';
import { ConfirmDialog } from '../../../components/ConfirmDialog';
import { CreatePublishableTokenDialog } from '../../../components/CreatePublishableTokenDialog';
import { CopyButton } from '../../../components/shared/CopyButton';

interface KeyRecord {
  kid: string;
  isAnonymous: boolean;
  name?: string | null;
  description?: string | null;
  token?: string | null;
  createdAt: string;
  updatedAt: string;
}

export const Route = createFileRoute('/projects/$projectId/admission-tokens')({
  component: ProjectAdmissionTokensTab,
});

function ProjectAdmissionTokensTab() {
  const { projectId } = useParams({ from: '/projects/$projectId/admission-tokens' });
  const project = useStore(s => s.projects[projectId]);

  const [keys, setKeys] = useState<KeyRecord[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [statusMessage, setStatusMessage] = useState('');

  // Publishable token creation dialog
  const [showCreateToken, setShowCreateToken] = useState(false);

  // Secret key creation form
  const [showCreateKey, setShowCreateKey] = useState(false);
  const [keyDescription, setKeyDescription] = useState('');
  const [isCreatingKey, setIsCreatingKey] = useState(false);
  const [newSecretKey, setNewSecretKey] = useState<string | null>(null);

  // Delete confirmation
  const [deleteTarget, setDeleteTarget] = useState<KeyRecord | null>(null);

  const publishableTokens = keys.filter(k => k.isAnonymous);
  const secretKeys = keys.filter(k => !k.isAnonymous);

  const loadKeys = useCallback(async () => {
    try {
      await waitForReady();
      const corr = uuidv4();
      send('project.jwks.list', { projectId }, corr);
      const result = await waitForAck<{ ok: boolean; keys: KeyRecord[] }>(corr);
      setKeys(result.keys);
    } catch (error: any) {
      setStatusMessage(error?.message || 'Failed to load keys');
    } finally {
      setIsLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    loadKeys();
  }, [loadKeys]);

  if (!project) return null;

  const handleTokenCreated = (record: KeyRecord) => {
    setKeys(prev => [record, ...prev]);
    setShowCreateToken(false);
    setStatusMessage('Publishable token created.');
  };

  const handleCreateKey = async () => {
    if (!keyDescription.trim()) return;

    setIsCreatingKey(true);
    setStatusMessage('');
    setNewSecretKey(null);

    try {
      await waitForReady();
      const corr = uuidv4();
      send('project.jwks.generate', { projectId, description: keyDescription.trim() }, corr);
      const result = await waitForAck<{
        ok: boolean;
        kid: string;
        privateJwkBase64: string;
        description: string;
        createdAt: string;
      }>(corr);

      setKeys(prev => [{
        kid: result.kid,
        isAnonymous: false,
        description: result.description,
        createdAt: result.createdAt,
        updatedAt: result.createdAt,
      }, ...prev]);

      setNewSecretKey(result.privateJwkBase64);
      setKeyDescription('');
      setShowCreateKey(false);
      setStatusMessage('Copy the secret key now. It will not be shown again.');
    } catch (error: any) {
      setStatusMessage(error?.message || 'Failed to create secret key');
    } finally {
      setIsCreatingKey(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;

    try {
      await waitForReady();
      const corr = uuidv4();
      send('project.jwks.delete', { projectId, kid: deleteTarget.kid }, corr);
      await waitForAck<{ ok: boolean; deleted: string }>(corr);

      setKeys(prev => prev.filter(k => k.kid !== deleteTarget.kid));
      setDeleteTarget(null);
      setStatusMessage('Key deleted.');
    } catch (error: any) {
      setStatusMessage(error?.message || 'Failed to delete key');
      setDeleteTarget(null);
    }
  };

  return (
    <div className={styles.wrapper}>
      {/* Publishable Tokens */}
      <div className={styles.keySection}>
        <div className={styles.keySectionHeader}>
          <div className={styles.keySectionTitle}>Publishable tokens</div>
          <button
            type="button"
            className={styles.keySectionAdd}
            onClick={() => setShowCreateToken(true)}
          >
            Create token
          </button>
        </div>

        {showCreateToken && (
          <CreatePublishableTokenDialog
            projectId={projectId}
            onClose={() => setShowCreateToken(false)}
            onCreated={handleTokenCreated}
          />
        )}

        {isLoading ? (
          <div className={styles.emptyState}>Loading...</div>
        ) : publishableTokens.length === 0 ? (
          <div className={styles.emptyState}>
            No publishable tokens yet.
          </div>
        ) : (
          publishableTokens.map(t => (
            <div key={t.kid} className={styles.tokenCard}>
              <div className={styles.tokenCardHeader}>
                <div className={styles.tokenCardInfo}>
                  <span className={styles.tokenCardName}>{t.name || t.kid}</span>
                  {t.description && <span className={styles.tokenCardDesc}>{t.description}</span>}
                </div>
                <div className={styles.tokenCardActions}>
                  <span className={styles.keyMeta}>{formatDate(t.createdAt)}</span>
                  <button
                    type="button"
                    className={`${styles.keyLink} ${styles.keyLinkDanger}`}
                    onClick={() => setDeleteTarget(t)}
                  >
                    Delete
                  </button>
                </div>
              </div>
              {t.token && (
                <div className={styles.tokenCardBody}>
                  <code className={styles.tokenCardCode}>{t.token}</code>
                  <CopyButton value={t.token} aria-label="Copy token" />
                </div>
              )}
            </div>
          ))
        )}
      </div>

      {/* Secret Keys */}
      <div className={styles.keySection}>
        <div className={styles.keySectionHeader}>
          <div className={styles.keySectionTitle}>Secret keys</div>
          {!showCreateKey && (
            <button
              type="button"
              className={styles.keySectionAdd}
              onClick={() => setShowCreateKey(true)}
            >
              Create key
            </button>
          )}
        </div>

        {showCreateKey && (
          <div className={styles.createForm}>
            <div className={styles.fieldGroup} style={{ marginTop: 0 }}>
              <label className={styles.label} htmlFor="key-description">Description</label>
              <input
                id="key-description"
                type="text"
                className={styles.input}
                value={keyDescription}
                onChange={e => setKeyDescription(e.target.value)}
                placeholder="e.g. Production backend signing key"
              />
            </div>
            <div className={styles.actions} style={{ marginTop: 0 }}>
              <button
                type="button"
                className={styles.button}
                onClick={handleCreateKey}
                disabled={isCreatingKey || !keyDescription.trim()}
              >
                {isCreatingKey ? 'Creating\u2026' : 'Create'}
              </button>
              <button
                type="button"
                className={`${styles.button} ${styles.buttonSecondary}`}
                onClick={() => { setShowCreateKey(false); setKeyDescription(''); }}
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {newSecretKey && (
          <div className={styles.secretCard} role="alert" aria-live="polite">
            <div className={styles.secretHeader}>
              <div className={styles.secretHeaderContent}>
                <div className={styles.secretTitle}>Secret Key (shown once)</div>
                <div className={styles.secretHint}>
                  Copy this key now. It will not be shown again.
                </div>
              </div>
              <div className={styles.actions} style={{ marginTop: 0 }}>
                <button
                  type="button"
                  className={styles.button}
                  onClick={() => copyText(newSecretKey).then(() => setStatusMessage('Secret key copied'))}
                >
                  Copy
                </button>
                <button
                  type="button"
                  className={`${styles.button} ${styles.buttonSecondary}`}
                  onClick={() => setNewSecretKey(null)}
                >
                  Dismiss
                </button>
              </div>
            </div>
            <pre className={styles.secretBody}>{newSecretKey}</pre>
          </div>
        )}

        {isLoading ? (
          <div className={styles.emptyState}>Loading...</div>
        ) : secretKeys.length === 0 ? (
          <div className={styles.emptyState}>
            No secret keys yet.
          </div>
        ) : (
          secretKeys.map(k => (
            <div key={k.kid} className={styles.keyRow}>
              <div className={styles.keyInfo}>
                <div className={styles.keyNameRow}>
                  <span className={styles.keyName}>{k.kid}</span>
                  <span className={styles.keyMeta}>{formatDate(k.createdAt)}</span>
                </div>
                {k.description && <div className={styles.keyDescription}>{k.description}</div>}
              </div>
              <button
                type="button"
                className={`${styles.keyLink} ${styles.keyLinkDanger}`}
                onClick={() => setDeleteTarget(k)}
              >
                Delete
              </button>
            </div>
          ))
        )}
      </div>

      {statusMessage && (
        <div className={layoutStyles.errorMessage}>
          <span className={layoutStyles.errorText}>{statusMessage}</span>
          <button
            type="button"
            onClick={() => setStatusMessage('')}
            className={layoutStyles.errorClose}
            aria-label="Dismiss status"
          >
            ×
          </button>
        </div>
      )}

      <ConfirmDialog
        isOpen={!!deleteTarget}
        title={deleteTarget?.isAnonymous ? 'Delete Publishable Token' : 'Delete Secret Key'}
        message={
          deleteTarget?.isAnonymous
            ? `Deleting "${deleteTarget?.name || deleteTarget?.kid}" will immediately invalidate this token. Any clients using it will stop working.`
            : `Deleting key "${deleteTarget?.kid}" will invalidate all tokens signed with this key. This cannot be undone.`
        }
        confirmText="Delete"
        cancelText="Cancel"
        onConfirm={handleDelete}
        onCancel={() => setDeleteTarget(null)}
        requireConfirmation={true}
        confirmationPhrase="I confirm"
      />
    </div>
  );
}

function formatDate(value: string): string {
  const date = new Date(value);
  return date.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

async function copyText(value: string): Promise<boolean> {
  try {
    if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(value);
      return true;
    }
  } catch {
    // ignore
  }
  return false;
}
